import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const HERO_IMAGES = [
  {
    url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/rw29451k_LOUPIAN.webp",
    site: "Loupian",
    title: { fr: "L'ouverture au monde", en: "Opening to the world" },
    quote: { fr: "Étang, horizon, lumière — le canal se dilue dans le monde.", en: "Lagoon, horizon, light — the canal dissolves into the world." },
    objectPosition: "center 55%",
  },
  {
    url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/166086f2_1000550422.webp",
    site: "Saint-Ferréol",
    title: { fr: "Le cœur du Fil", en: "The heart of the Thread" },
    quote: { fr: "Bassin de retenue, source du canal. Ici bat le cœur du Fil du Midi.", en: "Reservoir, source of the canal. Here beats the heart of the Fil du Midi." },
    objectPosition: "center 40%",
  },
  {
    url: "https://commons.wikimedia.org/wiki/Special:FilePath/Toulouse_le_canal_du_Midi.jpg?width=2400",
    site: "Toulouse",
    title: { fr: "Le nœud du Fil", en: "The knot of the Thread" },
    quote: { fr: "Ville rose, Garonne, Pont-Neuf — là où l'ingénieur rêva le canal.", en: "The pink city, the Garonne, the Pont-Neuf — where the engineer dreamed the canal." },
    objectPosition: "center 55%",
  },
  {
    url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/rkaa9hod_estr-tefonds-1.webp",
    site: "Castelnau d'Estrétefonds",
    title: { fr: "L'atelier du Fil", en: "The Thread's workshop" },
    quote: { fr: "Foyer des Arteurs. Le Fil s'enroule ici avant de rayonner.", en: "Home of Les Arteurs. The Thread coils here before radiating outward." },
    objectPosition: "center 75%",
  },
  {
    url: "https://commons.wikimedia.org/wiki/Special:FilePath/Castelnaudary_bassin_canal.jpg?width=2400",
    site: "Castelnaudary",
    title: { fr: "Le partage des eaux", en: "The watershed" },
    quote: { fr: "Grand Bassin — ligne de partage entre l'Atlantique et la Méditerranée.", en: "Grand Bassin — dividing line between the Atlantic and the Mediterranean." },
    objectPosition: "center 55%",
  },
  {
    url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/wli6as6z_le-tunnel-de-malpas.webp",
    site: "Le Malpas",
    title: { fr: "Le passage", en: "The passage" },
    quote: { fr: "Tunnel percé sous la montagne — le Fil traverse ce que rien ne devait traverser.", en: "A tunnel bored through the mountain — the Thread crosses what nothing was meant to cross." },
    objectPosition: "center center",
  },
];

function MaskLine({ children, delay = 0 }) {
  return (
    <span className="mask-line">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: [0.65, 0, 0.35, 1] }}
        className="block"
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setSlide((s) => (s + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(iv);
  }, []);

  return (
    <section ref={ref} className="relative min-h-screen w-full overflow-hidden" data-testid="hero">
      <motion.div
        className="absolute inset-0"
        style={{ y: imgY, scale: imgScale }}
      >
        <AnimatePresence>
          <motion.img
            key={slide}
            src={HERO_IMAGES[slide].url}
            alt={HERO_IMAGES[slide].site}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1.02 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.6, ease: "easeInOut" }, scale: { duration: 8, ease: "linear" } }}
            style={{ objectPosition: HERO_IMAGES[slide].objectPosition || "center center" }}
            className="absolute inset-0 w-full h-[115%] object-cover"
          />
        </AnimatePresence>
        <motion.div className="absolute inset-0 bg-fg" style={{ opacity: overlayOpacity }} />
      </motion.div>

      <div className="relative z-10 min-h-screen flex flex-col justify-between px-6 md:px-12 pt-32 pb-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="flex items-center gap-4 text-bg"
        >
          <span className="h-px w-16 bg-bg/60" />
          <span className="font-mono text-[11px] tracking-[0.24em] uppercase">
            Canal du Midi · UNESCO · 1996 → 2026
          </span>
        </motion.div>

        <div className="text-bg">
          <h1 className="font-serif font-light leading-[0.88] tracking-tight text-[clamp(2.75rem,10vw,10rem)]" aria-label="Le Fil du Midi. Canal'Art 2026">
            <MaskLine delay={0.2}>Le Fil</MaskLine>
            <MaskLine delay={0.4}><em className="font-light italic text-bg/90">du</em> Midi.</MaskLine>
            <MaskLine delay={0.65}><span className="text-cinabre">Canal'Art</span> 2026</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="mt-10 max-w-2xl text-bg/85 text-lg md:text-xl leading-relaxed font-serif italic"
          >
            {lang === "en" ? (
              <>The world's largest open-air art trail.
              From the Atlantic Ocean to the Mediterranean — 750 km, 6 sites, 30 years of UNESCO.</>
            ) : (
              <>Le plus grand parcours artistique à ciel ouvert du monde.
              De l'Océan Atlantique à la Méditerranée — 750 km, 6 sites, 30 ans UNESCO.</>
            )}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="flex items-end justify-between text-bg gap-6 flex-wrap"
        >
          <div className="grid grid-cols-3 gap-8 md:gap-16 border-t border-bg/30 pt-6 min-w-[280px]">
            {[
              ["30", lang === "en" ? "years UNESCO" : "ans UNESCO"],
              ["750", lang === "en" ? "km of trail" : "km de parcours"],
              ["06", lang === "en" ? "sites in 2026" : "sites 2026"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-serif text-4xl md:text-5xl leading-none">{n}</div>
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase mt-2 text-bg/70">{l}</div>
              </div>
            ))}
          </div>

          <a href="#manifeste" className="flex items-center gap-4 group" data-testid="hero-scroll-cue">
            <span className="font-mono text-[11px] tracking-[0.24em] uppercase text-bg/80">{lang === "en" ? "Scroll" : "Défiler"}</span>
            <span className="w-10 h-10 border border-bg/60 rounded-full flex items-center justify-center group-hover:border-cinabre group-hover:bg-cinabre transition-colors">
              <ArrowDown size={14} />
            </span>
          </a>
        </motion.div>

        {/* Slide poetic overlay — hidden on mobile to avoid overlap with the main title */}
        <div className="hidden lg:flex absolute right-6 md:right-12 top-1/2 -translate-y-1/2 items-center gap-6 text-bg pointer-events-none max-w-[520px] xl:max-w-[640px]">
          <div className="flex flex-col gap-2 shrink-0">
            {HERO_IMAGES.map((_, i) => (
              <span
                key={i}
                className={`w-[3px] transition-all duration-700 ${i === slide ? "h-16 bg-cinabre" : "h-8 bg-bg/40"}`}
              />
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={slide}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 1, ease: [0.65, 0, 0.35, 1] }}
              className="text-right"
            >
              <div className="font-mono text-[11px] xl:text-xs tracking-[0.32em] uppercase text-cinabre drop-shadow">
                § {HERO_IMAGES[slide].site}
              </div>
              <div className="mt-4 font-serif italic font-light text-4xl xl:text-6xl leading-[0.95] text-bg drop-shadow-lg">
                {HERO_IMAGES[slide].title[lang]}
              </div>
              <div className="mt-6 font-serif text-lg xl:text-2xl text-bg/90 leading-snug drop-shadow-md">
                « {HERO_IMAGES[slide].quote[lang]} »
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mobile-only: minimalist slide indicator at the bottom */}
        <div className="lg:hidden absolute bottom-6 left-6 right-6 flex items-center justify-between gap-4 text-bg pointer-events-none">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-bg/80">
            § {HERO_IMAGES[slide].site} · <span className="italic normal-case tracking-normal text-bg/90">{HERO_IMAGES[slide].title[lang]}</span>
          </div>
          <div className="flex gap-1.5 shrink-0">
            {HERO_IMAGES.map((_, i) => (
              <span
                key={i}
                className={`h-[2px] transition-all duration-700 ${i === slide ? "w-8 bg-cinabre" : "w-4 bg-bg/40"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
